import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { QuestionnaireType } from 'src/API';
import {
  DaySheetTemplateService,
  TemplateSignConfig
} from 'src/app/day-sheets/day-sheet-template.service';
import { DaySheetEyeMode, DaySheetTemplate } from 'src/app/day-sheets/day-sheet.types';
import { SchemaService } from 'src/app/shared/symptoms/services/schema/schema.service';
import { SymptomModes } from 'src/app/shared/symptoms/symptom.model';
import { Layout, LayoutsService } from '../layouts/layouts.service';

@Component({
  selector: 'csi-day-sheet-template-form-dialog',
  templateUrl: './day-sheet-template-form-dialog.component.html',
  styleUrls: ['./day-sheet-template-form-dialog.component.scss']
})
export class DaySheetTemplateFormDialogComponent implements OnInit {
  public nameControl = new FormControl('', Validators.required);
  public assessmentTypeControl = new FormControl(null, Validators.required);
  public signsLayoutFormControl = new FormControl(null);

  public signsLayout: Layout;
  public selectedQuestionnaires: QuestionnaireType[] = [];
  public isLoading = false;
  public readonly MAX_SIGNS = 8;
  public preventShowSignIds: string[] = [];

  public readonly questionnaireOptions: QuestionnaireType[] = [
    QuestionnaireType.DEQ,
    QuestionnaireType.SPEED,
    QuestionnaireType.SPEEDII,
    QuestionnaireType.OSDI,
    QuestionnaireType.OSDI6,
    QuestionnaireType.OSDIAndSPEED,
    QuestionnaireType.FITZPATRICK,
    QuestionnaireType.MEDICATION
  ];

  public readonly questionnaireLabels: Record<QuestionnaireType, string> = {
    [QuestionnaireType.DEQ]: 'DEQ',
    [QuestionnaireType.SPEED]: 'SPEED',
    [QuestionnaireType.SPEEDII]: 'SPEED II',
    [QuestionnaireType.OSDI]: 'OSDI',
    [QuestionnaireType.OSDI6]: 'OSDI-6',
    [QuestionnaireType.OSDIAndSPEED]: 'OSDI + SPEED',
    [QuestionnaireType.FITZPATRICK]: 'Fitzpatrick Skin Type',
    [QuestionnaireType.MEDICATION]: 'Medications',
    [QuestionnaireType.DERFS]: 'DERFS',
    [QuestionnaireType.CDERFS]: 'CDERFS',
    [QuestionnaireType.CUSTOM]: 'Custom',
    [QuestionnaireType.DEWSIII_Q]: 'DEWS III Questionnaire'
  };

  public readonly assessmentTypeOptions: SymptomModes[] = [
    SymptomModes.PreOpScreening,
    SymptomModes.PreOp,
    SymptomModes.Simple,
    SymptomModes.Silver,
    SymptomModes.Advanced,
    SymptomModes.Treatment,
    SymptomModes.FollowUp
  ];

  public readonly assessmentTypeLabels: Record<SymptomModes, string> = {
    [SymptomModes.PreOpScreening]: 'Pre-Operative Screening',
    [SymptomModes.PreOp]: 'ASCRS Pre-Op',
    [SymptomModes.Simple]: 'Screening',
    [SymptomModes.Silver]: 'Accelerated Dry Eye Assessment',
    [SymptomModes.Advanced]: 'Gold Standard',
    [SymptomModes.Treatment]: 'Treatment',
    [SymptomModes.FollowUp]: 'Follow Up',
    [SymptomModes.DewsIII]: 'DEWS III'
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DaySheetTemplate | null,
    private dialogRef: MatDialogRef<DaySheetTemplateFormDialogComponent>,
    private templateService: DaySheetTemplateService,
    private schemaService: SchemaService,
    private layoutsService: LayoutsService
  ) {}

  ngOnInit(): void {
    if (this.data) {
      this.nameControl.setValue(this.data.name || '');
      this.assessmentTypeControl.setValue(this.data.assessmentType || null);
      this.selectedQuestionnaires = this.templateService.parseQuestionnaires(
        this.data
      ) as QuestionnaireType[];

      // Compute preventShow before building signs layout so we can filter out prevented signs.
      this.computePreventShow(this.data.assessmentType as SymptomModes);

      const existingSigns = this.templateService.getTemplateSigns(this.data);
      const showIds = existingSigns
        .map(s => s.signId)
        .filter((id): id is string => id != null)
        .filter(id => !this.preventShowSignIds.includes(id));
      const eyeMode = existingSigns.reduce<Record<string, string>>(
        (m, s) => ({ ...m, [s.signId || '']: s.eyeMode || 'OD_OS' }),
        {}
      );
      this.signsLayout = {
        id: 'daySheet',
        name: 'Signs',
        signsLayout: { show: showIds, hide: [], eyeMode, preventShow: this.preventShowSignIds }
      };
    } else {
      this.signsLayout = {
        id: 'daySheet',
        name: 'Signs',
        signsLayout: { show: [], hide: [], eyeMode: {}, preventShow: this.preventShowSignIds }
      };
    }
    this.signsLayoutFormControl.setValue(this.signsLayout);

    this.assessmentTypeControl.valueChanges.subscribe(type => {
      this.computePreventShow(type);
    });
  }

  private computePreventShow(assessmentType: SymptomModes | null): void {
    if (!assessmentType) {
      this.preventShowSignIds = [];
      return;
    }
    const layout = this.layoutsService.allLayoutsById[assessmentType];
    const showInAssessment = layout && layout.signsLayout && layout.signsLayout.show;
    if (showInAssessment && showInAssessment.length > 0) {
      this.preventShowSignIds = this.schemaService.symptomKeys.filter(
        key => !showInAssessment.includes(key)
      );
    } else {
      this.preventShowSignIds = [];
    }
  }

  public isQuestionnaireSelected(type: QuestionnaireType): boolean {
    return this.selectedQuestionnaires.includes(type);
  }

  public toggleQuestionnaire(type: QuestionnaireType, checked: boolean): void {
    if (checked) {
      this.selectedQuestionnaires = [...this.selectedQuestionnaires, type];
    } else {
      this.selectedQuestionnaires = this.selectedQuestionnaires.filter(q => q !== type);
    }
  }

  public get canSave(): boolean {
    return this.nameControl.valid && this.assessmentTypeControl.valid && !this.isLoading;
  }

  public save(): void {
    if (!this.canSave) {
      return;
    }
    this.isLoading = true;

    const name: string = this.nameControl.value;
    const assessmentType: SymptomModes | null = this.assessmentTypeControl.value;

    const signsValue = this.signsLayoutFormControl.value;
    const showIds: string[] =
      signsValue && signsValue.signsLayout ? signsValue.signsLayout.show : [];
    const eyeModeMap: Record<string, string> =
      signsValue && signsValue.signsLayout ? signsValue.signsLayout.eyeMode : {};

    const newSigns: TemplateSignConfig[] = showIds.map((signId, i) => ({
      signId,
      label:
        (this.schemaService.symptomMap[signId] && this.schemaService.symptomMap[signId].name) ||
        signId,
      eyeMode: (eyeModeMap[signId] || DaySheetEyeMode.OD_OS) as DaySheetEyeMode,
      sortOrder: i + 1
    }));

    const op =
      this.data && this.data.id
        ? this.templateService.updateTemplate(
            this.data.id,
            name,
            this.selectedQuestionnaires,
            assessmentType,
            newSigns
          )
        : this.templateService.createTemplate(
            name,
            this.selectedQuestionnaires,
            assessmentType,
            newSigns
          );

    op.subscribe(
      result => {
        this.isLoading = false;
        this.dialogRef.close(result);
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }
}
